import { Box, styled, useTheme } from "@mui/material";
import { PickersDay } from "@mui/lab";
import { endOfWeek, startOfWeek } from "date-fns";
import { FC, PropsWithChildren } from "react";
import { PickersDayProps } from "@mui/lab/PickersDay";
import {
  dateSizeStyles,
  focusDisableStyles,
  generateLeftSelectedRange,
  generateRightSelectedRange,
  generateSelectedRange
} from "./calendars-style-utils";
import useDynamicLocale from "../../hooks/use-dynamic-locale";
import { isSameDay } from "../../utils";
import { StyledComponent } from "@mui/styles";

export const SelectableDay: StyledComponent<any> = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "isDayHighlighted"
})<{ isDayHighlighted?: boolean }>(({ isDayHighlighted, theme }) => ({
  ...dateSizeStyles(theme.shape.borderRadius as number),
  margin: 0.5, //small gap between dates for date picker
  ...focusDisableStyles(theme.palette),

  ":hover": { backgroundColor: theme.palette.grey[300] },
  backgroundColor: isDayHighlighted ? theme.palette.grey[700] : "inherit",
  color: isDayHighlighted ? theme.palette.common.white : "inherit",
  "&.Mui-disabled": {
    color: isDayHighlighted ? theme.palette.common.white : theme.palette.grey[400]
  },
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    ":hover": {
      backgroundColor: theme.palette.primary.main
    }
  }
}));

const StyledRangeDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "isDayHighlighted"
})<{
  values: Date[];
}>((props) => {
  const { values, day } = props;
  const date = day as Date;
  const isEndDate = isSameDay(values[0], date);
  const isStartDate = isSameDay(values[values.length - 1], date);

  return {
    ...dateSizeStyles(props.theme.shape.borderRadius as number),
    ...focusDisableStyles(props.theme.palette),

    "&.Mui-selected": {
      color: props.theme.palette.text.primary,
      "&.Mui-disabled": { color: props.theme.palette.grey[500] },
      backgroundColor: props.theme.palette.grey[100],
      ...((isStartDate || isEndDate) && {
        color: `${props.theme.palette.common.white}!important`,
        backgroundColor: props.theme.palette.primary.main
      })
    }
  };
});

interface PickersDayPropsWithDates extends PickersDayProps<unknown> {
  values: Date[];
}

export const SelectableRangeDay: FC<PropsWithChildren<PickersDayPropsWithDates>> = (props) => {
  const { values, day, selected } = props;
  const date = day as Date;
  const theme = useTheme();
  const isEndDate = isSameDay(values[0], date);
  const isStartDate = isSameDay(values[values.length - 1], date);
  const locale = useDynamicLocale();
  const isStartOfTheWeek = isSameDay(
    startOfWeek(date, {
      locale
    }),
    date
  );
  const isEndOfTheWeek = isSameDay(
    endOfWeek(date, {
      locale
    }),
    date
  );

  const selectedStyle = selected ? generateSelectedRange(theme.palette) : {};
  const endStyle =
    (isEndDate || isStartOfTheWeek) && selected
      ? generateRightSelectedRange(theme.palette, theme.shape.borderRadius as number)
      : {};
  const startStyle =
    (isStartDate || isEndOfTheWeek) && selected
      ? generateLeftSelectedRange(theme.palette, theme.shape.borderRadius as number)
      : {};

  return (
    <Box sx={[{ ...selectedStyle, ...endStyle, ...startStyle }]}>
      {/*// @ts-ignore*/}
      <StyledRangeDay {...props} />
    </Box>
  );
};
